import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { ExperimentProvider } from './contexts/ExperimentContext';
import { TrackerProvider } from './contexts/TrackerContext';
import ConsentForm from './components/phases/ConsentForm';
import PreExperiment from './components/phases/PreExperiment';
import Instructions from './components/phases/Instructions';
import EmailTaskIntro from './components/phases/EmailTaskIntro';
import EmailTask from './components/phases/EmailTask';
import DeviceTaskIntro from './components/phases/DeviceTaskIntro';
import DeviceTask from './components/phases/DeviceTask';
import CodingTaskIntro from './components/phases/CodingTaskIntro';
import CodingTask from './components/phases/CodingTask';
import InformationSharingTaskIntro from './components/phases/InformationSharingTaskIntro';
import InformationSharingTask from './components/phases/InformationSharingTask';
import InfoSharingConsent from './components/phases/InfoSharingConsent';
import InfoSharingPreSurvey from './components/phases/InfoSharingPreSurvey';
import InfoSharingDebrief from './components/phases/InfoSharingDebrief';
import FillerTask from './components/phases/FillerTask';
import PostTaskBattery from './components/phases/PostTaskBattery';
import PostSurveyIntro from './components/phases/PostSurveyIntro';
import Debrief from './components/phases/Debrief';
import Complete from './components/phases/Complete';
import CalibrationTask from './components/phases/CalibrationTask';
import MainTask from './components/phases/MainTask';
import Header from './components/common/Header';
import ProtectedRoute from './components/common/ProtectedRoute';
import FTTTestHarness from './components/dev/FTTTestHarness';
import FTTRealAnalysis from './components/dev/FTTRealAnalysis';
import AnalysisDashboard from './components/dev/AnalysisDashboard';
import DevNav from './components/dev/DevNav';
import FieldExperimentTracker from './components/FieldExperimentTracker';
import { initializeExperiment, savePhaseData, fetchActiveArm } from './services/api';
import { getOrgConfig } from './data/orgConfigs';
import { resolveEntryTask } from './utils/entryTask';
import { captureSonaId } from './utils/recruitment';
import './styles/App.css';

function App() {
  console.log('App component rendering, pathname:', window.location.pathname);

  // Fresh-start escape hatch (testing): ?reset=1 (or ?fresh=1) wipes the local
  // session/consent/completion state so a new run can begin from ANY browser —
  // incognito keeps localStorage alive while a window stays open, so completing a
  // run then reusing that window re-triggers the replay guard. Runs once,
  // synchronously, before any localStorage is read below. Harmless in production:
  // a real participant's link never carries this flag.
  useState(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get('reset') === '1' || sp.get('fresh') === '1') {
        ['experimentSession', 'participantId', 'hasConsented', 'consentedArm', 'infoSharingComplete', 'selectedTask', 'prolificId', 'consentStartTime', 'sonaId', 'prolificCompletionUrl', 'prolificCompletionCode', 'completionPlatform', 'completionFailed', 'assignedRotation', 'mainArmInterface']
          .forEach(k => localStorage.removeItem(k));
        Object.keys(localStorage)
          .filter(k => k.startsWith('infoSharingBackup_'))
          .forEach(k => localStorage.removeItem(k));
      }
      captureSonaId(window.location.search);
      // Stamp when this participant first landed (consent screen) — once per
      // session — so full in-app time (consent → debrief) can be computed server-side.
      if (!localStorage.getItem('consentStartTime')) {
        localStorage.setItem('consentStartTime', String(Date.now()));
      }
      // Capture Prolific URL params once at landing so they persist across the whole
      // flow (payment reconciliation + the end-of-study completion redirect). Applies
      // to every flow, including the phishing email/device/coding study.
      const ppid = sp.get('PROLIFIC_PID') || sp.get('prolific_pid');
      if (ppid) localStorage.setItem('prolificId', ppid);
      const pstudy = sp.get('STUDY_ID') || sp.get('study_id');
      if (pstudy) localStorage.setItem('prolificStudyId', pstudy);
      const psession = sp.get('SESSION_ID') || sp.get('session_id');
      if (psession) localStorage.setItem('prolificSessionId', psession);
    } catch { /* ignore */ }
    return null;
  });

  const [hasConsented, setHasConsented] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [experimentData, setExperimentData] = useState<any>(null);
  // Resolve the study task SYNCHRONOUSLY on first render so info-sharing
  // participants are routed to the info-sharing consent/pre-survey immediately
  // and never transiently see the phishing consent or the coder/non-coder
  // PreExperiment. URL ?task=infosharing wins and is persisted; else localStorage.
  const [selectedTask] = useState<string | null>(() => {
    try {
      const resolvedTask = resolveEntryTask(
        window.location.search,
        localStorage.getItem('selectedTask')
      );
      if (resolvedTask) localStorage.setItem('selectedTask', resolvedTask);
      return resolvedTask;
    } catch { return null; }
  });
  const isInfoSharing = selectedTask === 'infosharing';
  const [loading, setLoading] = useState(true);

  // Server-side arm switch: which flow a NEW arrival gets. A restored session uses
  // the arm pinned on its own participant record, never the current switch.
  const [activeArm, setActiveArm] = useState<string | null>(null);
  const [mainStudy, setMainStudy] = useState<string | undefined>(undefined);
  const [armError, setArmError] = useState<string | null>(null);
  const [armChanged, setArmChanged] = useState(false);
  const autoInitRef = React.useRef(false);
  useEffect(() => {
    if (localStorage.getItem('experimentSession')) return; // restored session: arm comes from the record
    fetchActiveArm().then(a => { setActiveArm(a.arm); setMainStudy(a.mainStudy); });
  }, []);
  // The verification study (main arm) and calibration both create the participant
  // right after consent — neither has the legacy PreExperiment survey.
  const autoInitArm = activeArm === 'calibration' || (activeArm === 'main' && mainStudy === 'verification');
  // Mobile is blocked for the verification study AND the calibration arm (advisor F19): the
  // inbox + destination pages need a desktop viewport.
  const isMobile = typeof window !== 'undefined' && (window.innerWidth < 900 || /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent));
  const blocksMobile = activeArm === 'calibration' || (activeArm === 'main' && mainStudy === 'verification');

  useEffect(() => {
    // Add dev mode class to body
    if (process.env.NODE_ENV === 'development') {
      document.body.classList.add('dev-mode');
    }
    
    const init = async () => {
      try {
        // Check for existing session
        const existingSession = localStorage.getItem('experimentSession');
        const existingConsent = localStorage.getItem('hasConsented');
        
        if (existingSession) {
          const data = JSON.parse(existingSession);
          setExperimentData(data);
          setIsInitialized(true);
          setHasConsented(true);
          // Restore participantId for TrackerContext
          if (data.participant?.id) {
            localStorage.setItem('participantId', data.participant.id);
          }
        } else if (existingConsent === 'true') {
          // User has consented but no session yet
          setHasConsented(true);
        }
      } catch (error) {
        console.error('Failed to restore session:', error);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const handleConsent = () => {
    console.log('User has provided consent');
    setHasConsented(true);
    localStorage.setItem('hasConsented', 'true');
    // Which study's consent was signed: the server refuses to start a different one (409).
    if (activeArm) localStorage.setItem('consentedArm', activeArm);
  };

  const handleInitialize = async (demographicData: any) => {
    try {
      console.log('Initializing experiment with demographics:', demographicData);
      
      // Backend now handles task assignment using database-backed logic
      // Pass org config so backend stores which org this participant belongs to
      const orgConfig = getOrgConfig();
      (demographicData as any).organizationId = orgConfig.orgId;
      (demographicData as any).organizationName = orgConfig.orgName;
      const data = await initializeExperiment(demographicData);
      console.log('Experiment initialized:', data);
      console.log('Task assignment from backend:', data.participant?.taskAssignment);
      setExperimentData(data);
      setIsInitialized(true);
      localStorage.setItem('experimentSession', JSON.stringify(data));
      // Store participantId for TrackerContext to use
      if (data.participant?.id) {
        localStorage.setItem('participantId', data.participant.id);

        // Save pre-experiment stratification data ONLY for the cohort-based
        // studies. Study 2 (info-sharing) has no coder/non-coder stratification,
        // and the calibration arm has no pre-survey at all, so skip both.
        if (demographicData.experimentType !== 'info-sharing' && demographicData.arm !== 'calibration' && !data.participant?.mainArmInterface) {
          try {
            const surveyData = {
              stratification: {
                workDivision: demographicData.workDivision,
                codingPercentage: demographicData.codingPercentage,
                yearsOfCoding: demographicData.yearsOfCoding
              }
            };

            await savePhaseData(data.participant.id, 'pre-experiment', surveyData);
            console.log('Pre-experiment stratification data saved');
          } catch (error) {
            console.error('Failed to save pre-experiment data:', error);
          }
        }
      }
      console.log('Navigation should happen now - isInitialized:', true);
    } catch (error) {
      console.error('Failed to initialize experiment:', error);
      const msg = String((error as any)?.message || '');
      if (msg.includes('503')) {
        setArmError('closed');
        return;
      }
      // 409 armChanged: the study switched (e.g. calibration → main) after this student read the
      // consent. Show the consent for the study they will actually do, then start again.
      if (msg.includes(' 409 ')) {
        let next: { arm?: string; mainStudy?: string } = {};
        try { next = JSON.parse(msg.slice(msg.indexOf('{'))); } catch { /* fall back to a fresh fetch */ }
        const a = next.arm ? next : await fetchActiveArm();
        localStorage.removeItem('hasConsented'); localStorage.removeItem('consentedArm');
        setActiveArm(a.arm || null); setMainStudy(a.mainStudy);
        if (a.arm === 'closed') setArmError('closed');
        setArmChanged(true); setHasConsented(false); autoInitRef.current = false;
        return;
      }
      alert('Failed to initialize experiment. Please check the console.');
    }
  };

  // Calibration / verification arms have no pre-survey: create the participant right after consent.
  useEffect(() => {
    if (hasConsented && !isInitialized && !isInfoSharing && autoInitArm && !autoInitRef.current) {
      autoInitRef.current = true;
      handleInitialize({ experimentType: 'phishing', arm: localStorage.getItem('consentedArm') || activeArm });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasConsented, isInitialized, isInfoSharing, autoInitArm]);

  const participantArm: string | undefined = experimentData?.participant?.arm;

  const renderRoot = () => {
    if (!hasConsented) {
      if (!isInfoSharing && (activeArm === 'closed' || armError === 'closed')) {
        return (
          <div className="consent-container"><div className="consent-form">
            <h1>Study Currently Full</h1>
            <p>This study is not accepting new participants right now. Please check back later or choose another study in SONA.</p>
            <p>No study record has been created and no time has been counted.</p>
          </div></div>
        );
      }
      if (!isInfoSharing && blocksMobile && isMobile) {
        return (
          <div className="consent-container"><div className="consent-form">
            <h1>Please Use a Laptop or Desktop</h1>
            <p>This study shows an email inbox and web pages that need a full-size screen. Please open your SONA link again on a laptop or desktop computer.</p>
            <p>No study record has been created and no time has been counted.</p>
          </div></div>
        );
      }
      if (isInfoSharing) return <InfoSharingConsent onConsent={handleConsent} />;
      return (
        <>
          {armChanged && <div className="arm-changed-note" data-testid="arm-changed-note">The study you are taking part in has been updated. Please read this consent form before you begin.</div>}
          <ConsentForm onConsent={handleConsent} arm={activeArm} mainStudy={mainStudy} />
        </>
      );
    }
    if (isInitialized) {
      if (isInfoSharing) {
        return localStorage.getItem('infoSharingComplete') === 'true'
          ? <Navigate to="/info-sharing-debrief" replace />
          : <Navigate to="/info-sharing-intro" replace />;
      }
      if (participantArm === 'calibration') return <Navigate to="/calibration" replace />;
      if (experimentData?.participant?.mainArmInterface) return <Navigate to="/main-task" replace />;
      return <Navigate to="/instructions" replace />;
    }
    if (isInfoSharing) return <InfoSharingPreSurvey onComplete={handleInitialize} />;
    if (armError === 'closed' || activeArm === 'closed') {
      return (
        <div className="consent-container"><div className="consent-form">
          <h1>Study Currently Full</h1>
          <p>This study is not accepting new participants right now. Please check back later or choose another study in SONA.</p>
        </div></div>
      );
    }
    if (autoInitArm) return <div className="loading">Setting up your session…</div>;
    if (activeArm === null) return <div className="loading">Loading…</div>;
    return <PreExperiment onComplete={handleInitialize} />;
  };

  if (loading) {
    return <div className="loading">Loading experiment...</div>;
  }

  // Remove the direct render test

  return (
    <Router basename="/ftt">
      <ExperimentProvider value={experimentData}>
        <TrackerProvider>
          <div className="app">
            <Header />
            {process.env.NODE_ENV === 'development' && <DevNav />}
            {process.env.NODE_ENV === 'development' && (
              <div style={{ padding: '10px', background: '#ffebcd', textAlign: 'center' }}>
                <button 
                  onClick={() => {
                    localStorage.clear();
                    window.location.href = '/';
                  }}
                  style={{ 
                    padding: '5px 15px', 
                    background: '#ff6b6b', 
                    color: 'white', 
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginRight: '10px'
                  }}
                >
                  Reset Experiment (Dev Only)
                </button>
                <button 
                  onClick={async () => {
                    try {
                      // Quick initialize with default data
                      const quickDemographics = {
                        age: '25-34',
                        gender: 'male',
                        education: 'bachelors',
                        computerExperience: 'advanced',
                        securityTraining: 'basic',
                        phishingExperience: 'once',
                        emailUsageFrequency: 'daily',
                        organizationSize: 'medium',
                        industry: 'technology',
                        jobRole: 'it-technical',
                        securityAttitude: '4',
                        perceivedControl: '4',
                        subjectiveNorms: '4',
                        behavioralIntention: '4',
                        perceivedSeverity: '4',
                        perceivedVulnerability: '3',
                        responseEfficacy: '4',
                        selfEfficacy: '4',
                        // Add any potentially missing fields
                        techExperience: '3',
                        occupation: 'it-technical',
                        emailUsage: 'daily',
                        securityIncidents: 'no'
                      };
                      
                      console.log('Quick Start: Setting consent...');
                      localStorage.setItem('hasConsented', 'true');
                      setHasConsented(true);
                      
                      console.log('Quick Start: Initializing experiment...');
                      await handleInitialize(quickDemographics);
                      console.log('Quick Start: Success!');
                    } catch (error) {
                      console.error('Quick Start failed:', error);
                      alert('Quick Start failed. Check console for details.');
                    }
                  }}
                  style={{ 
                    padding: '5px 15px', 
                    background: '#4CAF50', 
                    color: 'white', 
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Quick Start (Skip Forms)
                </button>
              </div>
            )}
            <div className="container">
              <Routes>
                <Route path="/" element={renderRoot()} />
                <Route
                  path="/main-task"
                  element={
                    <ProtectedRoute>
                      <MainTask />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/calibration"
                  element={
                    <ProtectedRoute>
                      <CalibrationTask />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/instructions"
                  element={
                    <ProtectedRoute>
                      <Instructions />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/email-task-intro"
                  element={
                    <ProtectedRoute>
                      <EmailTaskIntro />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/email-task"
                  element={
                    <ProtectedRoute>
                      <EmailTask />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/device-task-intro"
                  element={
                    <ProtectedRoute>
                      <DeviceTaskIntro />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/device-task"
                  element={
                    <ProtectedRoute>
                      <DeviceTask />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/coding-task-intro"
                  element={
                    <ProtectedRoute>
                      <CodingTaskIntro />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/coding-task"
                  element={
                    <ProtectedRoute>
                      <CodingTask />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/info-sharing-intro"
                  element={
                    <ProtectedRoute>
                      <InformationSharingTaskIntro />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/info-sharing-task"
                  element={
                    <ProtectedRoute>
                      <InformationSharingTask />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/info-sharing-debrief"
                  element={<InfoSharingDebrief />}
                />
                <Route
                  path="/filler-task"
                  element={
                    <ProtectedRoute>
                      <FillerTask />
                    </ProtectedRoute>
                  }
                />
                <Route 
                  path="/post-experiment" 
                  element={<PostSurveyIntro />} 
                />
                <Route 
                  path="/post-survey" 
                  element={<PostTaskBattery />} 
                />
                <Route 
                  path="/debrief" 
                  element={
                    <ProtectedRoute>
                      <Debrief />
                    </ProtectedRoute>
                  } 
                />
                <Route
                  path="/complete"
                  element={<Complete />}
                />
                {/* Field Experiment Tracking Route */}
                <Route
                  path="/field-track/:emailId"
                  element={<FieldExperimentTracker />}
                />
                {/* Development Routes */}
                {process.env.NODE_ENV === 'development' && (
                  <>
                    <Route 
                      path="/dev/ftt-test" 
                      element={<FTTTestHarness />} 
                    />
                    <Route 
                      path="/dev/ftt-analysis" 
                      element={<FTTRealAnalysis />} 
                    />
                    <Route 
                      path="/dev/analysis" 
                      element={<AnalysisDashboard />} 
                    />
                  </>
                )}
                <Route 
                  path="*" 
                  element={
                    <div>
                      <h2>Page not found</h2>
                      <p>Current path: {window.location.pathname}</p>
                      <p>Is initialized: {isInitialized ? 'Yes' : 'No'}</p>
                      <p>Experiment data: {experimentData ? 'Loaded' : 'Not loaded'}</p>
                    </div>
                  }
                />
              </Routes>
            </div>
          </div>
        </TrackerProvider>
      </ExperimentProvider>
    </Router>
  );
}

export default App;
